import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAnalytics } from '../../shared/analytics/create-events';
import * as analytics from '../../shared/analytics/analytics.json';
import { ModuleList } from '../../shared/analytics/module-list';
import { dashboardViewQuery } from '../../api/command-dashboard-api';
import {
  normalizeRows,
  useCommandDashboardTilesStore,
} from './command-dashboard-tiles-store';
import { TileDefinitionRows, TileTarget } from './grid-layout-math';
import {
  COMMAND_DASHBOARD_TILE_LIBRARY,
  CommandDashboardTileDefinition,
  CommandDashboardTileKey,
} from './tile-config';

export function useCommandDashboardActions() {
  const tileRowKeys = useCommandDashboardTilesStore((state) => state.tileRows);
  const addTileToStore = useCommandDashboardTilesStore(
    (state) => state.addTile
  );
  const placeTileInStore = useCommandDashboardTilesStore(
    (state) => state.placeTile
  );
  const moveTileInStore = useCommandDashboardTilesStore(
    (state) => state.moveTile
  );
  const removeTileFromStore = useCommandDashboardTilesStore(
    (state) => state.removeTile
  );
  const hydrate = useCommandDashboardTilesStore((state) => state.hydrate);
  const { createEvent } = useAnalytics();
  const { data: dashboardView, isFetched: dashboardViewFetched } = useQuery(
    dashboardViewQuery()
  );

  // Loads the user's working layout exactly once: from their active saved
  // view if they have one, otherwise the server-computed default tileRows
  // (the 3 seeded tiles for an existing user, or empty for a new one - see
  // DashboardViewResource#readOrEmpty). Guarded so a later background
  // refetch of dashboardViewQuery (e.g. on window focus) never stomps
  // in-progress, unsaved edits.
  const hasHydrated = useRef(false);
  // Set alongside the hydrate call, in the same effect, so callers can hold the
  // grid back until the layout is real. Until then tileRows is just the store's
  // empty starting value, which is indistinguishable from a genuinely empty
  // dashboard and would flash the empty state at every returning user.
  const [isLayoutLoaded, setIsLayoutLoaded] = useState(false);
  useEffect(() => {
    if (!dashboardViewFetched || hasHydrated.current) {
      return;
    }
    hasHydrated.current = true;

    const activeSavedView = dashboardView?.savedViews.find(
      (view) => view.name === dashboardView.activeView
    );
    hydrate(
      (activeSavedView?.tileRows ??
        dashboardView?.tileRows ??
        []) as CommandDashboardTileKey[][]
    );
    setIsLayoutLoaded(true);
  }, [dashboardView, dashboardViewFetched, hydrate]);

  // A tile the user lacks permission for stays on the dashboard rather than
  // being dropped - it renders in a degraded, permission-explaining state
  // instead of disappearing outright.
  const tileRows = useMemo<TileDefinitionRows>(
    () =>
      tileRowKeys.map((row) =>
        row
          .map((key) =>
            COMMAND_DASHBOARD_TILE_LIBRARY.find((tile) => tile.key === key)
          )
          .filter(
            (tile): tile is CommandDashboardTileDefinition => tile !== undefined
          )
      ),
    [tileRowKeys]
  );

  // Permission gating (disabled state + "you need X" reasoning) is TileLibrary's own concern -
  // this just filters out tiles already placed on the dashboard.
  const availableTiles = useMemo<CommandDashboardTileDefinition[]>(() => {
    const active = new Set(tileRowKeys.flat());
    return COMMAND_DASHBOARD_TILE_LIBRARY.filter(
      (tile) => !active.has(tile.key)
    );
  }, [tileRowKeys]);

  // Nothing here is persisted or announced - the working layout only ever
  // reaches the server when the user explicitly saves a dashboard view (see
  // dashboard-view-menu-button.tsx / save-dashboard-view-dialog.tsx), so
  // reordering/adding/removing tiles just tracks usage, silently.
  const trackTileAction = (eventKey: string) => {
    createEvent(eventKey, {
      module: ModuleList.modules.commandDashboard.name,
      section: ModuleList.modules.commandDashboard.sections.tileManagement,
    });
  };

  const addTile = (key: CommandDashboardTileKey) => {
    addTileToStore(key);
    trackTileAction(analytics.command_dashboard.tile_management.add_tile);
  };

  const placeTile = (key: CommandDashboardTileKey, target: TileTarget) => {
    placeTileInStore(key, target);
    trackTileAction(analytics.command_dashboard.tile_management.add_tile);
  };

  const removeTile = (key: CommandDashboardTileKey) => {
    removeTileFromStore(key);
    trackTileAction(analytics.command_dashboard.tile_management.remove_tile);
  };

  const moveTile = (key: CommandDashboardTileKey, target: TileTarget) => {
    moveTileInStore(key, target);
    trackTileAction(analytics.command_dashboard.tile_management.reorder_tiles);
  };

  const activeSavedView = dashboardView?.savedViews.find(
    (view) => view.name === dashboardView.activeView
  );
  const savedTileRowKeys =
    activeSavedView?.tileRows ?? dashboardView?.tileRows ?? [];
  const isDirty = useMemo(
    () =>
      JSON.stringify(normalizeRows(tileRowKeys)) !==
      JSON.stringify(normalizeRows(savedTileRowKeys)),
    [tileRowKeys, savedTileRowKeys]
  );

  return {
    isLayoutLoaded,
    isDirty,
    tileRows,
    availableTiles,
    addTile,
    placeTile,
    moveTile,
    removeTile,
  };
}
